import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Edit,
  Building2,
  Users,
  User,
  Settings,
  ShieldCheck,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const InfoRow = ({ icon: Icon, label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-sm font-medium text-slate-900">{value}</p>
      </div>
    </div>
  );
};

const getEnrollmentStatusClass = (status) => {
  if (status === 'completed') return 'bg-emerald-100 text-emerald-700';
  if (status === 'in_progress') return 'bg-blue-100 text-blue-700';
  return 'bg-slate-100 text-slate-700';
};

export default function EmployerDetail() {
  const { employerId } = useParams();
  const navigate = useNavigate();
  const [employer, setEmployer] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEmployerDetail();
  }, [employerId]);

  const loadEmployerDetail = async () => {
    try {
      const [employerData, employeesData] = await Promise.all([
        api.entities.Employer.filter({ id: employerId }),
        api.entities.Employee.filter({ employer_id: employerId })
      ]);

      if (employerData && employerData.length > 0) {
        setEmployer(employerData[0]);
      } else {
        toast.error('Employer not found');
      }
      setEmployees(employeesData || []);
    } catch (err) {
      console.error('[loadEmployerDetail] Error:', err);
      toast.error('Failed to load employer details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!employer) {
    return (
      <div className="space-y-6">
        <Button variant="outline" onClick={() => navigate('/Employers')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Employers
        </Button>
        <Card>
          <CardContent className="p-12 text-center text-slate-500">
            Employer not found.
          </CardContent>
        </Card>
      </div>
    );
  }

  const activeEmployeeCount = employees.filter((e) => e.is_active !== false).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Button variant="outline" onClick={() => navigate('/Employers')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Employers
        </Button>
        <Button onClick={() => navigate(`/Employers?edit=${employer.id}`)}>
          <Edit className="w-4 h-4 mr-2" />
          Edit Employer
        </Button>
      </div>

      {/* Profile Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-white font-bold text-2xl flex-shrink-0">
              {employer.company_name?.[0] || 'C'}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-slate-900">{employer.company_name}</h1>
              <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-slate-500">
                <span>{employer.industry || 'Industry not set'}</span>
                {employer.contact_name && (
                  <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{employer.contact_name}</span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge className={employer.is_active !== false ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                {employer.is_active !== false ? 'Active' : 'Inactive'}
              </Badge>
              <Badge className="bg-blue-100 text-blue-700">
                <Users className="w-3 h-3 mr-1" />
                {activeEmployeeCount} Employees
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Contact & Address */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-500" />
              Contact & Address
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-slate-100">
            <InfoRow icon={User} label="Contact Name" value={employer.contact_name} />
            <InfoRow icon={Mail} label="HR Contact Email" value={employer.hr_contact_email} />
            <InfoRow icon={Phone} label="Phone" value={employer.phone} />
            <InfoRow
              icon={MapPin}
              label="Address"
              value={[employer.address, employer.city, employer.state, employer.zip_code].filter(Boolean).join(', ') || null}
            />
          </CardContent>
        </Card>

        {/* Policies */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-500" />
              Policies
            </CardTitle>
          </CardHeader>
          <CardContent className="divide-y divide-slate-100">
            <InfoRow icon={Settings} label="Employer Wait Period" value={`${employer.employer_wait_period_days ?? 60} days`} />
            <InfoRow icon={Settings} label="Default Enrollment Window" value={`${employer.default_enrollment_window_days ?? 30} days`} />
            <InfoRow icon={Settings} label="QLE Enrollment Window" value={`${employer.qle_enrollment_window_days ?? 60} days`} />
            <InfoRow icon={Settings} label="Late Enrollment Grace Period" value={`${employer.late_enrollment_grace_days ?? 30} days`} />
            <InfoRow icon={ShieldCheck} label="Allow QLE Enrollment" value={employer.allow_qle_enrollment !== false ? 'Yes' : 'No'} />
            <InfoRow icon={ShieldCheck} label="Require Dependent Verification" value={employer.enforce_dependent_verification ? 'Yes' : 'No'} />
            <InfoRow icon={ShieldCheck} label="Require EOI for Late Enrollment" value={employer.require_evidence_of_insurability_for_late_enrollment !== false ? 'Yes' : 'No'} />
          </CardContent>
        </Card>

        {/* Rules & Limits */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-purple-500" />
              Rules & Limits
            </CardTitle>
          </CardHeader>
          <CardContent className="grid sm:grid-cols-2 lg:grid-cols-3 divide-y sm:divide-y-0 divide-slate-100">
            <InfoRow icon={DollarSign} label="Default Admin Fee" value={`$${Number(employer.default_admin_fee ?? 60).toFixed(2)}/mo`} />
            <InfoRow icon={DollarSign} label="Premium Claimable %" value={`${(Number(employer.flex_claimable_percentage ?? 0.86) * 100).toFixed(0)}%`} />
            <InfoRow icon={DollarSign} label="Premium Minimum" value={`$${Number(employer.flex_amount_min ?? 600).toLocaleString()}`} />
            <InfoRow icon={DollarSign} label="Premium Maximum" value={`$${Number(employer.flex_amount_max ?? 2500).toLocaleString()}`} />
            <InfoRow icon={DollarSign} label="Calculation Increment" value={`$${Number(employer.flex_calculation_increment ?? 50).toLocaleString()}`} />
            <InfoRow icon={DollarSign} label="EOI Coverage Threshold" value={`$${Number(employer.eoi_coverage_threshold ?? 50000).toLocaleString()}`} />
            <InfoRow icon={Settings} label="Document Retention" value={`${employer.document_retention_years ?? 7} years`} />
          </CardContent>
        </Card>

        {/* Employees */}
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-500" />
              Employees ({employees.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {employees.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">No employees on file for this employer yet.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {employees.map((employee) => (
                  <div
                    key={employee.id}
                    className="flex items-center justify-between gap-3 py-3 cursor-pointer hover:bg-slate-50 -mx-2 px-2 rounded"
                    onClick={() => navigate(`/EmployeeDetail/${employee.id}`)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                        {employee.first_name?.[0]}{employee.last_name?.[0]}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {employee.first_name} {employee.last_name}
                        </p>
                        <p className="text-xs text-slate-500 truncate">{employee.user_email}</p>
                      </div>
                    </div>
                    <Badge className={getEnrollmentStatusClass(employee.enrollment_status)}>
                      {(employee.enrollment_status || 'not_started').replace(/_/g, ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
